import 'react-native-get-random-values'; // Required for uuid in React Native / Expo

import { Platform } from 'react-native';
import { isWeb } from 'smoothie';
import { v4 as uuidv4 } from 'uuid';

import { FRUITFUL_ENV } from '../config/config';

export const NOT_NUMBERS_REGEX = /^(\D*)$/;

export const uuid = uuidv4; // Always use this exported instance of uuid, which will have imported and initialized the 'react-native-get-random-values' package above.

export const isEmpty = (value: unknown): boolean => {
    return (
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0) ||
        value === '' ||
        Object.keys(value).length === 0
    );
};

export const getOrdinalSuffix = (number: number): string => {
    if (number % 100 >= 11 && number % 100 <= 13) {
        return 'th';
    }
    switch (number % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
};

export const formatDateWithOrdinalSuffix = (
    date: Date,
    options: Intl.DateTimeFormatOptions,
): string => {
    const day = date.getDate();
    const ordinalSuffix = getOrdinalSuffix(day);

    const formattedDate = date.toLocaleString('en-US', options);

    // Insert the ordinal suffix after the day
    return formattedDate.replace(/\b(\d+)\b/, `$1${ordinalSuffix}`);
};

export const getDayFromOrdinalNumber = (value: string | undefined): string => {
    const ordinalRegex = /^(\d{1,2})(st|nd|rd|th)$/;
    const match = value?.match(ordinalRegex) ?? null;

    if (match && match.length > 1 && match[1] !== undefined) {
        return match[1];
    }
    return '';
};

export const noop = (): void => {};
// for use with Array.prototype.sort()
export const sortByProperty = <T>(a: T, b: T, property: keyof T): number => {
    if (a[property] < b[property]) return -1;
    if (a[property] > b[property]) return 1;
    return 0;
};

// adds https scheme to url
export const withHttps = (url: string) =>
    !/^https?:\/\//i.test(url) ? `https://${url}` : url;

export const getAppScheme = () => {
    return `com.fruitful${getAppSchemeSuffix()}`;
};

const getAppSchemeSuffix = () => {
    switch (FRUITFUL_ENV) {
        case 'production':
            return Platform.OS === 'android' ? '.fruitful' : '';
        case 'staging':
            return '.stg';
        case 'local':
        case 'test':
        case 'sandbox':
            return '.dev';
        default:
            return '.unknown';
    }
};

export const getAppOrigin = () => {
    return isWeb ? `${window.location.origin}/` : `${getAppScheme()}://`;
};

export const isEmail = (input: string) => {
    return (
        (/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.exec(input) ?? []).length >
        0
    );
};

export function buildQueryParams<
    QueryParams = Record<
        string,
        bigint | boolean | number | string | symbol | Array<number | string>
    >,
>(query: QueryParams): string {
    const searchParams = new URLSearchParams();

    for (const paramName in query) {
        const value = query[paramName];

        searchParams.append(paramName, value?.toString() ?? '');
    }

    return `?${searchParams.toString()}`;
}

export const assertNotNullish = <TYPE>(
    value: TYPE | null | undefined,
): TYPE => {
    if (value === null) {
        throw Error('Unexpected null value.');
    } else if (value === undefined) {
        throw Error('Unexpected undefined value.');
    }
    return value;
};

export const getRandomNumber = (min: number, max: number): number =>
    Math.round(Math.random() * (max - min) + min);

export const getObjectKeyFromValue = <T>(
    map: Record<string, T>,
    value: T,
): string | undefined => {
    const entry = Object.entries(map).find(([_, v]) => v === value);
    return entry?.[0];
};

// Avoids converting 0 to -0 (which is unfortunately a thing in javascript)
export const convertToNegative = (value?: number): number =>
    value != null && value !== 0 ? value * -1 : 0;

/**
 * @param oneOrMoreWords A string of one or more words
 * @returns The provided string with the first letter of each word capitalized, and the rest of each word unchanged
 */
export const properCase = (oneOrMoreWords: string): string => {
    if (!oneOrMoreWords) return oneOrMoreWords;

    return oneOrMoreWords
        .split(/([ -])/) // Split on spaces and hyphens
        .map((word) => {
            // Don't lowercase the rest of the string in case it has special casing already applied
            // e.g. "McDonald" should not be converted to "Mcdonald", "HSA Bank" should not be converted to "HSA bank"
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join('');
};
